import { type Quarry } from '@common/types/quarryType';
import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import { quarriesApi } from '@/api/queries';
import { boostApi } from '@/api/queries/boost';
import { sanitizeBoosts } from '@/common/lib/sanitizeBoosts';
import { type BoostItemType, type BoostType } from '@/common/types/factoryType';
import { type ClaimResourceByIdResponse } from '@/common/types/query/quarry';

interface quarryState {
  quarries: Quarry | null;
  tap: Record<string, number>;
}

const initialState: quarryState = {
  quarries: null,
  tap: {},
};

const quarrySlice = createSlice({
  name: 'userQuarry',
  initialState,
  reducers: {
    updateResourceStock: (
      state,
      { payload }: PayloadAction<{ quarryId: string; minedResources: number }>,
    ) => {
      if (state.quarries?.userQuarries) {
        const updatedQuarries = state.quarries.userQuarries.map((quarry) => {
          if (quarry.id === payload.quarryId) {
            if (quarry.storageCapacity) {
              return {
                ...quarry,
                localStock:
                  (quarry.localStock ?? 0) >= quarry.storageCapacity
                    ? quarry.storageCapacity
                    : payload.minedResources,
              };
            }
          }

          return quarry;
        });

        state.quarries.userQuarries = updatedQuarries;
      }
    },
    mineResource: (state, { payload }: PayloadAction<string>) => {
      if (state.quarries?.userQuarries) {
        const quarry = state.quarries.userQuarries.find(
          ({ id }) => payload === id,
        );

        if (
          quarry?.localStock !== undefined &&
          quarry?.storageCapacity !== undefined
        ) {
          if (quarry.localStock < quarry.storageCapacity) {
            quarry.localStock += 1;
          }
        }
      }
    },
    tapResource: (
      state,
      { payload }: PayloadAction<{ id: string; quantity: number }>,
    ) => {
      if (state.quarries) {
        const quarry = state.quarries.userQuarries.find(
          ({ id }) => id === payload.id,
        );

        if (quarry) {
          const resourceToAdd = quarry.resourcesPerTap * payload.quantity;
          const result = quarry.localStock + resourceToAdd;
          quarry.localStock =
            result >= quarry.storageCapacity ? quarry.storageCapacity : result;

          if (payload.id in state.tap) {
            state.tap[payload.id] += payload.quantity;
          } else {
            state.tap[payload.id] = payload.quantity;
          }
        }
      }
    },
    clearQuarryBoost: (
      state,
      { payload }: PayloadAction<{ plantId: string; boostType: BoostType }>,
    ) => {
      if (state?.quarries?.boosts) {
        const boost = state.quarries.boosts.find(
          (boost) =>
            boost?.boostType === payload.boostType &&
            boost?.plants?.includes(payload.plantId),
        );

        if (boost) {
          boost.expiresInSeconds = 0;
        }
      }
    },
    countDowntQuarryBoostTimers: (state) => {
      const boosts = state.quarries?.boosts;

      if (boosts) {
        for (const boost of boosts) {
          if (!boost) continue;

          if ((boost.expiresInSeconds || 0) > 0) {
            boost.expiresInSeconds = (boost.expiresInSeconds || 0) - 1;
          }

          if ((boost.nextUseInSeconds || 0) > 0) {
            boost.nextUseInSeconds = (boost.nextUseInSeconds || 0) - 1;
          }
        }
      }
    },
    buyCryptoBoostsQuarry: (
      state,
      { payload }: PayloadAction<BoostItemType[]>,
    ) => {
      if (state.quarries) {
        const incoming =
          sanitizeBoosts<BoostItemType>(
            payload,
            'quarry/buyCryptoBoostsQuarry',
          ) || [];
        state.quarries.boosts = state.quarries.boosts
          ? [
              ...(sanitizeBoosts<BoostItemType>(
                state.quarries.boosts,
                'quarry/buyCryptoBoostsQuarry/existing',
              ) || []),
              ...incoming,
            ]
          : incoming;
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addMatcher(
        quarriesApi.endpoints.buildQuarry.matchFulfilled,
        (state, action) => {
          if (state.quarries?.userQuarries && action.payload) {
            state.quarries.userQuarries = action.payload;
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.upgradeQuarry.matchFulfilled,
        (state, { payload }) => {
          if (payload && state.quarries) {
            const quarryToUpdate = state.quarries.userQuarries.find(
              (el) => el.id === payload.id,
            );

            if (quarryToUpdate) {
              Object.assign(quarryToUpdate, payload);
            }
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.claimResourceById.matchFulfilled,
        (state, { payload }: PayloadAction<ClaimResourceByIdResponse>) => {
          // state.loading = false;
          const quarries = state.quarries?.userQuarries;

          if (quarries) {
            const quarryToUpdate = quarries.find(
              (quarry) => quarry.id === payload.plant.id,
            );

            if (quarryToUpdate) {
              Object.assign(quarryToUpdate, payload.plant);
            }
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.getUserQuarriesForClaim.matchFulfilled,
        (state, { payload }) => {
          const quarriesList = state.quarries?.userQuarries;
          state.quarries = {
            ...payload,
            boosts: sanitizeBoosts<BoostItemType>(
              payload.boosts,
              'quarry/getUserQuarriesForClaim',
            ),
            userQuarries: payload.userQuarries.map((item) => {
              if (item.id in state.tap && state.tap[item.id] > 0) {
                const tap = state.tap[item.id];
                const quarry = quarriesList?.find(({ id }) => item.id === id);

                if (quarry) {
                  //add resources that have not yet been sent with the request /quarry/boost
                  return {
                    ...item,
                    localStock: item.localStock + tap * item.resourcesPerTap,
                  };
                }
              }

              return item;
            }),
          };
        },
      )
      .addMatcher(
        isAnyOf(
          boostApi.endpoints.boostPlant.matchFulfilled,
          boostApi.endpoints.boostPlantAdvertising.matchFulfilled,
        ),
        (state, action) => {
          const plantType = action.meta.arg.originalArgs.plantType;

          if (plantType === 'Quarry') {
            const payload = action.payload as Quarry;

            const quarriesList = state.quarries?.userQuarries;
            state.quarries = {
              ...payload,
              boosts: sanitizeBoosts<BoostItemType>(
                payload.boosts,
                'quarry/boostPlant',
              ),
              userQuarries: payload.userQuarries.map((item) => {
                if (item.id in state.tap && state.tap[item.id] > 0) {
                  const tap = state.tap[item.id];
                  const quarry = quarriesList?.find(({ id }) => item.id === id);

                  if (quarry) {
                    //add resources that have not yet been sent with the request /quarry/boost
                    return {
                      ...item,
                      localStock: item.localStock + tap * item.resourcesPerTap,
                    };
                  }
                }

                return item;
              }),
            };
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.claimAllResources.matchFulfilled,
        (state, { payload }) => {
          if (state.quarries) {
            state.quarries.userQuarries = payload.plants;
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.tapQuarry.matchFulfilled,
        (state, { payload, meta }) => {
          if (state.quarries?.userQuarries) {
            const quarry = state.quarries.userQuarries.find(
              ({ id }) => id === payload.quarry.id,
            );

            if (quarry) {
              //no need to update the localStock, for all mined resource will be send request
              // const updatedQuarry: UserQuarry = {
              //   ...payload.quarry,
              //   localStock: quarry.localStock,
              // };

              Object.assign(quarry, payload.quarry);

              if (payload.quarry.id in state.tap) {
                state.tap[payload.quarry.id] -=
                  meta.arg.originalArgs?.boosts || 1;
              }
            }
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.tapQuarry.matchRejected,
        (state, { meta }) => {
          const args = meta.arg.originalArgs;

          if (state.quarries?.userQuarries) {
            const quarry = state.quarries.userQuarries.find(
              ({ id }) => id === args.quarryId,
            );

            if (quarry) {
              //remove taped resources
              quarry.localStock -= quarry.resourcesPerTap * (args.boosts || 1);

              if (args.quarryId in state.tap) {
                state.tap[args.quarryId] -= meta.arg.originalArgs?.boosts || 1;
              }
            }
          }
        },
      );
  },
});

export const {
  updateResourceStock,
  mineResource,
  tapResource,
  clearQuarryBoost,
  countDowntQuarryBoostTimers,
  buyCryptoBoostsQuarry,
} = quarrySlice.actions;
export default quarrySlice.reducer;
